import React, { useState, useEffect } from 'react';
import {
  getCookiePreferences,
  saveCookieConsent,
  getBasicStats,
  COOKIE_TYPES,
  COOKIE_CONFIG,
  hasCookieConsent
} from '../utils/cookies';
import { useLanguage } from '../contexts/LanguageContext';

const CookieManager = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [preferences, setPreferences] = useState({});
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setPreferences(getCookiePreferences());
    setStats(getBasicStats());
  }, []);

  const handlePreferenceChange = (type, checked) => {
    setPreferences(prev => ({
      ...prev,
      [type]: checked
    }));
  };

  const handleSavePreferences = async () => {
    await saveCookieConsent(preferences);
    onClose();
  };

  const handleResetConsent = () => {
    if (confirm(t('cookieManager.preferences.resetConfirm'))) {
      localStorage.removeItem('conexao-saude-cookie-consent');
      localStorage.removeItem('conexao-saude-cookie-preferences');
      window.location.reload();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('cookieManager.title')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Status */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">{t('cookieManager.status.title')}</h3>
            <p className="text-sm text-gray-600">
              {t('cookieManager.status.consent')}: <span className={`font-medium ${hasCookieConsent() ? 'text-green-600' : 'text-red-600'}`}>
                {hasCookieConsent() ? t('cookieManager.status.granted') : t('cookieManager.status.notGranted')}
              </span>
            </p>
            {stats && (
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center">
                  <span className="text-gray-600">{t('cookieManager.stats.pageViews')}:</span>
                  <span className="font-bold ml-2 text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">{stats.totalPageViews}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-600">{t('cookieManager.stats.linkClicks')}:</span>
                  <span className="font-bold ml-2 text-blue-600 bg-blue-50 px-2 py-1 rounded-full">{stats.totalLinkClicks}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-600">{t('cookieManager.stats.uniquePages')}:</span>
                  <span className="font-bold ml-2 text-purple-600 bg-purple-50 px-2 py-1 rounded-full">{stats.uniquePages}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-600">{t('cookieManager.stats.lastActivity')}:</span>
                  <span className="font-medium ml-2 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                    {stats.lastActivity ? new Date(stats.lastActivity).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Preferences */}
          <div className="space-y-4 mb-6">
            <h3 className="font-semibold text-gray-900">{t('cookieManager.preferences.title')}</h3>
            {Object.entries(COOKIE_CONFIG).map(([type, config]) => (
              <div key={type} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900">{config.name}</h4>
                    {config.required && (
                      <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                        {t('cookieManager.preferences.necessary')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{config.description}</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences[type] || false}
                  onChange={(e) => handlePreferenceChange(type, e.target.checked)}
                  disabled={config.required}
                  className="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleResetConsent}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
            >
              {t('cookieManager.preferences.reset')}
            </button>
            <button
              onClick={handleSavePreferences}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-lg transition-all duration-200 shadow-lg"
            >
              {t('cookieManager.preferences.save')}
            </button>
          </div>

          {/* Info */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">{t('cookieManager.info.title')}</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              {(t('cookieManager.info.items') || []).map((item, index) => (
                <li key={index}>• {item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieManager;
